import { Component, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import * as fromSettings from '../../company-setting';
import { IUser, IUserInfo } from '../../user-info';
import * as fromUser from '../../user-info/store/user.reducer';
import {
    distinctUntilKeyChanged,
    filter,
    map,
    take,
    takeWhile,
    withLatestFrom
} from 'rxjs/operators';
import { Process } from '../../shared';
import { StatusData } from '../../data-services/dashboard/status';
import { Contract } from '../../data-services/contract';
import * as fromDataServices from '../../data-services';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import clone from 'just-clone';
import { Funding } from '../../data-services/dashboard/funding/funding.model';
import * as fromLookups from '../../lookups';
import { IUserRole } from '../../user-role';
import * as fromUserRole from '../../user-role/store/reducers';
import * as fromUserSetting from '../../user-setting/store';
import { Accrual } from '../../data-services/dashboard/accrual';
import { MessageStateService } from '../../shared/services/message-state.service';

@Component({
    selector: 'ef-dashboard-feature',
    templateUrl: './dashboard-feature.component.html',
    styleUrls: ['./dashboard-feature.component.scss'],
    standalone: false
})
export class DashboardFeatureComponent implements OnInit {
    active = true;
    public contracts$: Observable<Contract[]>;
    public companySettings$: Observable<fromSettings.CompanySetting[]>;
    public accrualData$: Observable<Accrual[]>;
    public statusData$: Observable<StatusData[]>;
    public funding$: Observable<Funding[]>;
    public drStatus$: Observable<fromDataServices.DRStatus.DRStatus[]>;
    public user$: Observable<IUser>;
    public userInfo$: Observable<IUserInfo>;
    public userRoles$: Observable<IUserRole[]>;
    public users$: Observable<Array<fromLookups.IDirectReport>>;
    public userSettings$: Observable<Array<fromUserSetting.UserSetting>>;
    public dashboardSettings$: Observable<
        fromDataServices.DashboardSettings.DashboardSettings[]
    >;
    roleID: number;

    adminMsg: string;
    mgrMsg: string;
    userMsg: string;
    announcement: string;
    announcementAdmin: string;

    showPMStatusBlock: boolean;
    showSupervisorStatusBlock: boolean;

    processes: Process[] = [];

    contractDataService: EntityCollectionService<fromDataServices.Contract.Contract>;
    roleStatusDataService: EntityCollectionService<fromDataServices.RoleStatus.StatusData>;
    fundingDataService: EntityCollectionService<fromDataServices.Funding.Funding>;
    drStatusDataService: EntityCollectionService<fromDataServices.DRStatus.DRStatus>;
    accrualDataService: EntityCollectionService<fromDataServices.Accrual.Accrual>;
    dashboardSettingsDataService: EntityCollectionService<fromDataServices.DashboardSettings.DashboardSettings>;
    contractFunding: ContractFunding[];

    selectedProcesses: Process[] = [];

    user: IUser;

    constructor(
        private store: Store,
        public entityServices: EntityServices,
        private messageStateService: MessageStateService
    ) {
        this.contractDataService =
            entityServices.getEntityCollectionService<fromDataServices.Contract.Contract>(
                fromDataServices.Contract.entityName
            );
        this.roleStatusDataService =
            entityServices.getEntityCollectionService<fromDataServices.RoleStatus.StatusData>(
                fromDataServices.RoleStatus.entityName
            );
        this.fundingDataService =
            entityServices.getEntityCollectionService<fromDataServices.Funding.Funding>(
                fromDataServices.Funding.entityName
            );
        this.drStatusDataService =
            entityServices.getEntityCollectionService<fromDataServices.DRStatus.DRStatus>(
                fromDataServices.DRStatus.entityName
            );
        this.accrualDataService =
            entityServices.getEntityCollectionService<fromDataServices.Accrual.Accrual>(
                fromDataServices.Accrual.entityName
            );
        this.dashboardSettingsDataService =
            entityServices.getEntityCollectionService<fromDataServices.DashboardSettings.DashboardSettings>(
                fromDataServices.DashboardSettings.entityName
            );

        this.user$ = this.store.pipe(select(fromUser.getUser));
        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));
    }

    ngOnInit(): void {
        this.users$ = this.store.pipe(
            select(fromLookups.getDirectReportsLookup)
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'announcement'
            })
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'announcement_admin'
            })
        );
        this.userRoles$ = this.store.pipe(select(fromUserRole.getUserRoles));
        this.user$.pipe(takeWhile(() => this.active)).subscribe(user => {
            this.user = user;
        });
        this.userInfo$
            .pipe(
                takeWhile(() => this.active),
                distinctUntilKeyChanged('DefaultRoleGroupID')
            )
            .subscribe(userInfo => {
                this.roleID = userInfo.DefaultRoleGroupID;
                if (userInfo.EmployeeListID != null) {
                    this.roleStatusDataService.getWithQuery(
                        `?UserListId=${userInfo.EmployeeListID}&RoleId=${userInfo.DefaultRoleGroupID}`
                    );
                    console.log('rolestatus: rolestatus call completed');
                } else {
                    console.log('rolestatus: employeelistid is undefined');
                }
            });
        // this.store
        //     .pipe(
        //         select(fromUser.getUserInfo),
        //         takeWhile(() => this.active),
        //         distinctUntilKeyChanged('DefaultRoleGroupID')
        //     )
        //     .subscribe(userInfo => {
        //         this.roleID = userInfo.DefaultRoleGroupID;
        //         this.roleStatusDataService.getWithQuery(
        //             `?UserListId=${userInfo.EmployeeListID}&RoleId=${userInfo.DefaultRoleGroupID}`
        //         );
        //     });

        this.dashboardSettingsDataService.getWithQuery(`?employeeListId=`);
        this.contractDataService.getAll();
        this.setupObservables();
        this.setupSubscriptions();
    }

    ngOnDestroy() {
        this.dashboardSettingsDataService.clearCache();
        this.drStatusDataService.clearCache();
        this.roleStatusDataService.clearCache();
        this.fundingDataService.clearCache();
        this.accrualDataService.clearCache();
        this.active = false;
    }

    setupObservables() {
        this.userSettings$ = this.store.pipe(select(fromUserSetting.selectAll));
        this.companySettings$ = this.store.pipe(select(fromSettings.selectAll));

        this.contracts$ = this.contractDataService.entities$.pipe(
            map(x => clone(x)),
            map(x =>
                x.sort((a, b) => a.ContractDesc.localeCompare(b.ContractDesc))
            )
        );
        this.statusData$ = this.roleStatusDataService.entities$.pipe(
            map(x => clone(x))
        );
        this.funding$ = this.fundingDataService.entities$.pipe(
            map(x => clone(x))
        );
        this.drStatus$ = this.drStatusDataService.entities$.pipe(
            map(x => clone(x))
        );
        this.accrualData$ = this.accrualDataService.entities$.pipe(
            map(x => clone(x))
        );
        this.dashboardSettings$ =
            this.dashboardSettingsDataService.entities$.pipe(
                map(x => clone(x))
            );
    }

    setupSubscriptions() {
        this.userSettings$
            .pipe(takeWhile(_ => this.active))
            .subscribe(userSettings => {
                // User settings are now handled by the MessageStateService
                // No need to manually track showMsg here
            });
        this.companySettings$
            .pipe(takeWhile(() => this.active))
            .subscribe(settings => {
                if (settings && settings.length > 0) {
                    if (
                        settings.findIndex(
                            x => x.SettingType == 'announcement'
                        ) != -1
                    ) {
                        this.announcement = settings.find(
                            x => x.SettingType == 'announcement'
                        ).SettingValue;
                    }
                    if (
                        settings.findIndex(
                            x => x.SettingType == 'announcement_admin'
                        ) != -1
                    ) {
                        this.announcementAdmin = settings.find(
                            x => x.SettingType == 'announcement_admin'
                        ).SettingValue;
                    }
                    if (this.roleID != null) {
                        if (this.roleID == 1) {
                            if (
                                settings.findIndex(
                                    x => x.SettingType == 'AdminMsg1'
                                ) != -1
                            ) {
                                this.adminMsg = settings.find(
                                    x => x.SettingType == 'AdminMsg1'
                                ).SettingValue;
                            }

                            if (
                                settings.findIndex(
                                    x => x.SettingType == 'MgrMsg1'
                                ) != -1
                            ) {
                                this.mgrMsg = settings.find(
                                    x => x.SettingType == 'MgrMsg1'
                                ).SettingValue;
                            }

                            if (
                                settings.findIndex(
                                    x => x.SettingType == 'UserMsg1'
                                ) != -1
                            ) {
                                this.userMsg = settings.find(
                                    x => x.SettingType == 'UserMsg1'
                                ).SettingValue;
                            }
                        } else if (
                            this.roleID == 12024 ||
                            this.roleID == 12025
                        ) {
                            this.adminMsg = '';

                            if (
                                settings.findIndex(
                                    x => x.SettingType == 'MgrMsg1'
                                ) != -1
                            ) {
                                this.mgrMsg = settings.find(
                                    x => x.SettingType == 'MgrMsg1'
                                ).SettingValue;
                            }

                            if (
                                settings.findIndex(
                                    x => x.SettingType == 'UserMsg1'
                                ) != -1
                            ) {
                                this.userMsg = settings.find(
                                    x => x.SettingType == 'UserMsg1'
                                ).SettingValue;
                            }
                        } else {
                            this.adminMsg = '';

                            this.mgrMsg = '';

                            if (
                                settings.findIndex(
                                    x => x.SettingType == 'UserMsg1'
                                ) != -1
                            ) {
                                this.userMsg = settings.find(
                                    x => x.SettingType == 'UserMsg1'
                                ).SettingValue;
                            }
                        }
                    }
                }

                // Calculate message count and update the service
                this.updateMessageCount();
            });

        combineLatest([this.userRoles$, this.user$])
            .pipe(takeWhile(() => this.active))
            .subscribe(([userRoles, user]) => {
                if (
                    userRoles.findIndex(x => x.RoleGroupID == 12024) != -1 &&
                    user.userInfo.DefaultRoleGroupID == 12024
                ) {
                    this.showSupervisorStatusBlock = true;
                } else {
                    this.showSupervisorStatusBlock = false;
                }
                if (
                    userRoles.findIndex(x => x.RoleGroupID == 12025) != -1 &&
                    user.userInfo.DefaultRoleGroupID == 12025
                ) {
                    this.showPMStatusBlock = true;
                } else {
                    this.showPMStatusBlock = false;
                }
                if (
                    userRoles.findIndex(x => x.RoleGroupID == 1) != -1 &&
                    user.userInfo.DefaultRoleGroupID == 1
                ) {
                    if (
                        userRoles.findIndex(x => x.RoleGroupID == 12025) != -1
                    ) {
                        this.showPMStatusBlock = true;
                    } else {
                        this.showPMStatusBlock = false;
                    }
                    if (
                        userRoles.findIndex(x => x.RoleGroupID == 12024) != -1
                    ) {
                        this.showSupervisorStatusBlock = true;
                    } else {
                        this.showSupervisorStatusBlock = false;
                    }
                }

                this.selectedProcesses = [];
                user.UserProcessCategories.forEach(cat => {
                    cat.UserProcesses.forEach(process => {
                        this.processes.push(process);
                    });
                });
                const home = {
                    ProcessID: 0,
                    ProcessName: 'Main',
                    RouteName: 'Main',
                    ProcessDesc: '',
                    ProcessTitle: 'Home',
                    MsgCount: null
                };
                const auths = this.processes.find(x => x.ProcessID == 2);
                const allUsers = this.processes.find(x => x.ProcessID == 14);
                const timeentry = this.processes.find(x => x.ProcessID == 64);
                const leave = this.processes.find(x => x.ProcessID == 69);
                const expentry = this.processes.find(x => x.ProcessID == 70);
                const timeapproval = this.processes.find(
                    x => x.ProcessID == 85
                );
                const expapproval = this.processes.find(x => x.ProcessID == 86);
                const timevalidate = this.processes.find(
                    x => x.ProcessID == 87
                );
                const expvalidate = this.processes.find(x => x.ProcessID == 88);
                const checkup = this.processes.find(x => x.ProcessID == 145);
                if (
                    userRoles.findIndex(x => x.RoleGroupID == 1) != -1 &&
                    user.userInfo.DefaultRoleGroupID == 1
                ) {
                    this.selectedProcesses.push(home);

                    this.selectedProcesses.push(allUsers);

                    // var contractsandfunding = this.processes.find(x => x.ProcessID == 13);
                    // this.selectedProcesses.push(contractsandfunding);

                    this.selectedProcesses.push(auths);

                    this.selectedProcesses.push(timeentry);
                    if (
                        this.processes.findIndex(x => x.ProcessID == 85) != -1
                    ) {
                        this.selectedProcesses.push(timeapproval);
                    }

                    this.selectedProcesses.push(checkup);
                } else if (
                    userRoles.findIndex(x => x.RoleGroupID == 12024) != -1 &&
                    user.userInfo.DefaultRoleGroupID == 12024
                ) {
                    this.selectedProcesses.push(home);
                    if (this.processes.findIndex(x => x.ProcessID == 2) != -1) {
                        this.selectedProcesses.push(auths);
                    }
                    this.selectedProcesses.push(timeentry);

                    this.selectedProcesses.push(timeapproval);

                    if (
                        this.processes.findIndex(x => x.ProcessID == 86) != -1
                    ) {
                        this.selectedProcesses.push(expapproval);
                    }
                } else if (
                    userRoles.findIndex(x => x.RoleGroupID == 12025) != -1 &&
                    user.userInfo.DefaultRoleGroupID == 12025
                ) {
                    this.selectedProcesses.push(home);

                    this.selectedProcesses.push(timeentry);

                    this.selectedProcesses.push(timevalidate);

                    if (
                        this.processes.findIndex(x => x.ProcessID == 88) != -1
                    ) {
                        this.selectedProcesses.push(expvalidate);
                    }
                } else {
                    this.selectedProcesses.push(home);

                    this.selectedProcesses.push(timeentry);

                    if (
                        this.processes.findIndex(x => x.ProcessID == 70) != -1
                    ) {
                        this.selectedProcesses.push(expentry);
                    }

                    if (
                        this.processes.findIndex(x => x.ProcessID == 69) != -1
                    ) {
                        this.selectedProcesses.push(leave);
                    }
                }
            });
    }

    onGetAccrualData($event) {
        this.accrualDataService.clearCache();
        this.accrualDataService.getAll();
    }
    onGetFunding($event) {
        this.fundingDataService.clearCache();
        this.fundingDataService.getWithQuery($event);
    }
    onGetDRStatus($event) {
        this.drStatusDataService.clearCache();
        this.drStatusDataService.getWithQuery($event);
    }
    onGetDirectReports($event) {
        this.store.dispatch(new fromLookups.LoadDirectReports($event));
    }
    onUpdateDashboardSettings($event) {
        this.dashboardSettingsDataService.upsert($event);
    }

    private updateMessageCount() {
        let messageCount = 0;

        // Count non-empty messages based on user role
        if (this.roleID === 1) {
            if (this.adminMsg && this.adminMsg.trim() !== '') messageCount++;
            if (this.mgrMsg && this.mgrMsg.trim() !== '') messageCount++;
            if (this.userMsg && this.userMsg.trim() !== '') messageCount++;
        } else if (this.roleID === 12024 || this.roleID === 12025) {
            if (this.mgrMsg && this.mgrMsg.trim() !== '') messageCount++;
            if (this.userMsg && this.userMsg.trim() !== '') messageCount++;
        } else {
            if (this.userMsg && this.userMsg.trim() !== '') messageCount++;
        }

        // Update the message state service
        this.messageStateService.setMessageCount(messageCount);
    }
}
export interface ContractFunding {
    Contract: string;
    ContractValue: number;
    ContractFunded: number;
    ContractSpent: number;
}
